'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'
import { Icons } from '@/components/ui/icons'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  BADGE_NOUN,
  isAdminRoles,
  sectionTitleForHref,
  visibleNavSections,
  type NavBadgeKey,
  type NavSection,
} from '@/lib/nav-items'
import { useShellCounts } from '@/hooks/useShellCounts'
import { BRAND_NAME, PRODUCT_SUBTITLE } from '@/lib/naming'
import { DEMO_FIRST_NAME, DEMO_INITIALS, DEMO_ROLE_LINE } from '@/lib/demo-identity'

// ---------------------------------------------------------------------------
// Rail state: collapsed or expanded, and which folded groups are open. Both
// persist in localStorage (read after mount so the first paint matches the
// server). Every access is wrapped: storage can be blocked or throw.
// ---------------------------------------------------------------------------

const COLLAPSED_KEY = 'pa.rail.collapsed'
const OPEN_SECTIONS_KEY = 'pa.rail.open-sections'

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Storage is a convenience; the rail works without it.
  }
}

interface SidebarContextType {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  toggle: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setCollapsedState] = useState(false)

  useEffect(() => {
    if (readStorage(COLLAPSED_KEY) === '1') setCollapsedState(true)
  }, [])

  const setIsCollapsed = useCallback((collapsed: boolean) => {
    setCollapsedState(collapsed)
    writeStorage(COLLAPSED_KEY, collapsed ? '1' : '0')
  }, [])

  const toggle = useCallback(
    () => setIsCollapsed(!isCollapsed),
    [isCollapsed, setIsCollapsed]
  )

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Nav row
// ---------------------------------------------------------------------------

/** Shared rich tooltip body: what this page does. */
function NavTooltipBody({
  label,
  description,
  badge,
}: {
  label: string
  description?: string
  badge?: number
}) {
  return (
    <div className='max-w-[15rem]'>
      <p className='text-sm font-semibold'>{label}</p>
      {badge !== undefined && (
        <p className='mt-0.5 text-xs font-medium text-white/80'>{badge} waiting</p>
      )}
      {description && (
        <p className='mt-0.5 text-xs font-normal leading-snug text-white/90'>
          {description}
        </p>
      )}
    </div>
  )
}

interface NavLinkProps {
  href: string
  icon: React.ElementType
  label: string
  description?: string
  isCollapsed: boolean
  badge?: number
  /** What the badge counts, for a screen reader: "live" or "waiting". */
  badgeNoun?: string
}

function NavLink({
  href,
  icon: Icon,
  label,
  description,
  isCollapsed,
  badge,
  badgeNoun = 'waiting',
}: NavLinkProps) {
  const pathname = usePathname()
  const isActive = pathname === href

  const link = (
    <Link
      href={href}
      data-nav-link
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'group relative flex items-center rounded-xl py-2',
        isCollapsed ? 'justify-center px-2' : 'gap-3 px-3',
        'text-[15px] font-medium transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
        isActive
          ? 'bg-white/[0.14] text-white'
          : 'text-white/70 hover:bg-white/[0.07] hover:text-white'
      )}
    >
      {isActive && (
        <span
          aria-hidden='true'
          className='absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-full bg-brand-cornflower'
        />
      )}
      <Icon
        strokeWidth={1.5}
        aria-hidden='true'
        className={cn(
          'h-[18px] w-[18px] shrink-0',
          isActive ? 'text-white' : 'text-white/60 group-hover:text-white'
        )}
      />
      {!isCollapsed && <span className='flex-1 truncate'>{label}</span>}
      {badge !== undefined && badge > 0 && (
        <>
          <span
            aria-hidden='true'
            className={cn(
              isCollapsed
                ? 'absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-brand-cornflower'
                : 'ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-cornflower/30 px-1.5 text-xs font-semibold text-white'
            )}
          >
            {!isCollapsed && (badge > 99 ? '99+' : badge)}
          </span>
          <span className='sr-only'>, {badge} {badgeNoun}</span>
        </>
      )}
    </Link>
  )

  return (
    <Tooltip delayDuration={isCollapsed ? 0 : 300}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side='right' className='ml-2'>
        <NavTooltipBody
          label={label}
          description={description}
          badge={isCollapsed ? badge : undefined}
        />
      </TooltipContent>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function SectionTitle({
  section,
  open,
  onToggle,
}: {
  section: NavSection
  open: boolean
  onToggle: () => void
}) {
  const id = `rail-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`
  const titleClass =
    'px-3 text-xs font-semibold uppercase tracking-[0.14em] text-white/55'

  if (!section.collapsedByDefault) {
    return <p className={cn(titleClass, 'mb-1.5')}>{section.title}</p>
  }

  return (
    <button
      type='button'
      aria-expanded={open}
      aria-controls={id}
      onClick={onToggle}
      data-nav-link
      className={cn(
        titleClass,
        'mb-1.5 flex w-full items-center gap-1.5 rounded-lg py-1 text-left',
        'hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
      )}
    >
      {section.title}
      {open ? (
        <Icons.chevronDown className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
      ) : (
        <Icons.chevronRight className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
      )}
    </button>
  )
}

function SidebarUser({ isCollapsed }: { isCollapsed: boolean }) {
  const { data: session } = useSession()
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  const email = session?.user?.email ?? undefined

  return (
    <div
      className={cn(
        'flex items-center gap-3',
        isCollapsed && 'justify-center'
      )}
    >
      <span
        title={email}
        className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-purple text-xs font-semibold text-white'
      >
        {DEMO_INITIALS}
      </span>
      {!isCollapsed && (
        <>
          <div className='min-w-0 flex-1'>
            <p className='truncate text-sm font-semibold text-white'>
              {DEMO_FIRST_NAME}
            </p>
            <p className='text-xs leading-snug text-white/65'>{DEMO_ROLE_LINE}</p>
          </div>
          {session?.user && (
            <button
              type='button'
              onClick={() => {
                window.location.href = `${basePath}/api/auth/logout`
              }}
              className='shrink-0 rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
              aria-label='Sign out'
              title='Sign out'
            >
              <Icons.logout className='h-4 w-4' aria-hidden='true' />
            </button>
          )}
        </>
      )}
    </div>
  )
}

export function Sidebar() {
  const { isCollapsed, setIsCollapsed } = useSidebar()
  const { data: session } = useSession()
  const pathname = usePathname()
  const counts = useShellCounts()
  const navRef = useRef<HTMLElement>(null)

  const isAdmin = isAdminRoles(session?.roles)
  const sections = visibleNavSections(isAdmin)

  // Which folded groups the viewer has opened. The group holding the active
  // route is always open, so the active item is never hidden.
  const [openSections, setOpenSections] = useState<string[]>([])
  useEffect(() => {
    const stored = readStorage(OPEN_SECTIONS_KEY)
    if (!stored) return
    try {
      const parsed: unknown = JSON.parse(stored)
      if (Array.isArray(parsed)) {
        setOpenSections(parsed.filter((v): v is string => typeof v === 'string'))
      }
    } catch {
      // A bad stored value reads as "nothing opened".
    }
  }, [])

  const activeSection = sectionTitleForHref(pathname)
  const isOpen = (section: NavSection) =>
    !section.collapsedByDefault ||
    isCollapsed ||
    openSections.includes(section.title) ||
    activeSection === section.title

  const toggleSection = (title: string) => {
    const next = openSections.includes(title)
      ? openSections.filter((t) => t !== title)
      : [...openSections, title]
    setOpenSections(next)
    writeStorage(OPEN_SECTIONS_KEY, JSON.stringify(next))
  }

  // Up and down arrows move between the rail's links and group buttons.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const targets = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>('[data-nav-link]') ?? []
    )
    const index = targets.indexOf(document.activeElement as HTMLElement)
    if (index < 0) return
    event.preventDefault()
    const next = index + (event.key === 'ArrowDown' ? 1 : -1)
    targets[(next + targets.length) % targets.length]?.focus()
  }

  return (
    <TooltipProvider>
      <aside
        role='navigation'
        aria-label='Main navigation'
        className={cn(
          'hidden flex-col md:flex',
          'fixed bottom-0 left-0 top-0 z-fixed',
          'bg-brand-navy text-white',
          'transition-[width] duration-300 ease-out',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div
          className={cn(
            'flex h-20 shrink-0 items-center px-4',
            isCollapsed && 'justify-center px-2'
          )}
        >
          <Link
            href='/'
            className='group flex items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
          >
            <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white'>
              <Logomark variant='dark' size={24} />
            </span>
            {!isCollapsed && (
              <span className='flex flex-col'>
                <span className='font-display text-[17px] font-semibold leading-tight text-white'>
                  {BRAND_NAME}
                </span>
                <span className='text-xs font-semibold uppercase leading-snug tracking-[0.12em] text-white/55'>
                  {PRODUCT_SUBTITLE}
                </span>
              </span>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav
          ref={navRef}
          aria-label='Primary navigation'
          onKeyDown={onKeyDown}
          className='scrollbar-hide flex-1 overflow-y-auto px-3 pb-4 pt-1'
        >
          {sections.map((section, sectionIndex) => {
            const open = isOpen(section)
            const id = `rail-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`
            return (
              <div
                key={section.title}
                className={cn(sectionIndex > 0 && (isCollapsed ? 'mt-3 border-t border-white/10 pt-3' : 'mt-5'))}
              >
                {!isCollapsed && (
                  <SectionTitle
                    section={section}
                    open={open}
                    onToggle={() => toggleSection(section.title)}
                  />
                )}
                {open && (
                  <div id={id} className='space-y-0.5'>
                    {section.items.map((item) => (
                      <NavLink
                        key={item.href}
                        href={item.href}
                        icon={item.icon}
                        label={item.label}
                        description={item.description}
                        isCollapsed={isCollapsed}
                        badge={item.badgeKey ? counts[item.badgeKey as NavBadgeKey] : undefined}
                        badgeNoun={(item.badgeKey ? BADGE_NOUN[item.badgeKey] : undefined) ?? 'waiting'}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* User block, then the collapse control */}
        <div className='shrink-0 border-t border-white/10 px-4 pb-2 pt-3'>
          <SidebarUser isCollapsed={isCollapsed} />
        </div>
        <div className={cn('shrink-0 px-3 pb-3', isCollapsed && 'flex justify-center')}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type='button'
                onClick={() => setIsCollapsed(!isCollapsed)}
                className='flex h-8 w-8 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isCollapsed ? (
                  <Icons.panelOpen className='h-4 w-4' aria-hidden='true' />
                ) : (
                  <Icons.panelClose className='h-4 w-4' aria-hidden='true' />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side='right' className='ml-2'>
              {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            </TooltipContent>
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  )
}
